import { Injectable, Provider, inject } from '@angular/core';

import { BalanceEntity } from '@app/domain/models/balance-entity.model';
import { SpeiContextActivator } from '@app/domain/ports/spei/spei-context-activator.port';
import { StorageEncryptor } from '@app/domain/ports/storage-encryptor.port';
import { BrowserStorageService } from '@app/services/browser-storage.service';
import { AvailableAmountService } from '@app/shared/services/available-amount.service';
import { ProviderContextService } from '@app/core/services/provider-context.service';
import { SpeiAssignedEntry, SpeiAssignedLevel } from '@app/domain/models/spei/spei-assigned-entry.model';

/** Llave de selección de cada nivel: las MISMAS que escribe el `company-selector`. */
const SELECCION_POR_NIVEL: Record<SpeiAssignedLevel, string> = {
  company: 'selectedCompany',
  unit: 'selectedBusinessUnit',
  account: 'selectedAccount',
};

/**
 * Activador de contexto SPEI sobre la mecánica legacy (feature 152).
 *
 * - `activateFather` es el cuerpo de `ProductSelectionComponent.selectProvider`
 *   MOVIDO tal cual (antes de esta feature elegir proveedor hacía esto en el acto).
 * - `activateCompany` es la rama `Empresa` de
 *   `CompanySelectorComponent.onEntityNameClick` + la cuenta padre preservada en
 *   `selectedFatherAccount` + la marca "la sesión eligió contexto SPEI".
 *
 * Mismo formato cifrado (`StorageEncryptor`) que los demás escritores de estas
 * llaves. El `company-selector` NO se migra a este puerto (deuda anotada).
 */
@Injectable({ providedIn: 'root' })
export class LegacySpeiContextActivator extends SpeiContextActivator {
  private readonly browserStorage = inject(BrowserStorageService);
  private readonly encryption = inject(StorageEncryptor);
  private readonly availableAmount = inject(AvailableAmountService);
  private readonly providerContext = inject(ProviderContextService);

  activateFather(father: BalanceEntity): void {
    const cifrado = this.encryption.encrypt(JSON.stringify([father]));
    this.browserStorage.setSessionStorageItem('balance_object', cifrado);
    this.browserStorage.setSessionStorageItem('selectedFatherAccount', cifrado);
    this.availableAmount.setBalanceInfo(father);

    // El sidebar usa esta llave para saber que ya hubo eleccion manual.
    const polipay = String(father.polipay_account ?? '').trim();
    if (polipay) sessionStorage.setItem('selectedCompanyId', polipay);
  }

  activateCompany(father: BalanceEntity, company: BalanceEntity): void {
    // La empresa hereda el riel de su cuenta padre: los hijos anidados no lo repiten.
    const provider = company.provider ?? father.provider;
    const entityForStorage: BalanceEntity = provider ? { ...company, provider } : { ...company };

    this.write('selectedFatherAccount', [father]);
    this.write('selectedCompany', company);
    this.write('balance_object', [entityForStorage]);
    this.availableAmount.setBalanceInfo(entityForStorage);
    this.providerContext.markSpeiChosen(father);
  }

  /**
   * R20: la entidad asignada (Empresa, Unidad o Cuenta de un riel sin cuenta padre) es
   * el contexto. Lleva su riel EFECTIVO (el login pudo pisar `provider` con el de un
   * gestor TPV). Se escribe la llave de selección de SU nivel y se quitan las otras dos.
   * ⚠️ `selectedFatherAccount` y `father_accounts_list` NO se tocan: no hay cuenta padre
   * en esa sesión y escribir una sería inventarla (E8).
   */
  activateAssignedEntity(entry: SpeiAssignedEntry): void {
    const entidad: BalanceEntity = { ...entry.entity, provider: entry.rail };
    const propia = SELECCION_POR_NIVEL[entry.level];
    this.write('balance_object', [entidad]);
    this.write(propia, entidad);
    for (const llave of Object.values(SELECCION_POR_NIVEL)) {
      if (llave !== propia) this.browserStorage.removeSessionStorageItem(llave);
    }
    this.availableAmount.setBalanceInfo(entidad);
    // Marca "eligió contexto SPEI": polipay o, sin él, la CLABE (sin ninguno no escribe).
    this.providerContext.markSpeiChosen(entidad);
  }

  private write(key: string, value: unknown): void {
    this.browserStorage.setSessionStorageItem(key, this.encryption.encrypt(JSON.stringify(value)));
  }
}

/** Enlace del puerto. Va en la raíz (`app.config.ts`). */
export function provideSpeiContextActivator(): Provider[] {
  return [{ provide: SpeiContextActivator, useExisting: LegacySpeiContextActivator }];
}
